import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader, Plus, Trash2 } from 'lucide-react'
import { useEffect, useTransition } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import {
  CREATE_USAGE_ALERT_CONFIG_MUTATION,
  UPDATE_USAGE_ALERT_CONFIG_MUTATION,
  USAGE_ALERT_CONFIG_QUERY,
} from '@/components/shared/usage-alert-card/query'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useMixpanel } from '@/lib/hooks'
import type {
  CreateUsageAlertConfigMutation,
  CreateUsageAlertConfigMutationVariables,
  UpdateUsageAlertConfigMutation,
  UpdateUsageAlertConfigMutationVariables,
} from '@/components/shared/usage-alert-card/query.types.gen'

type ExistingConfig = {
  id: string
  limitUsd: number
  emails: string[]
  triggers: { percentage: number }[]
}

type Props = {
  isOpen: boolean
  onClose: () => void
  idCompany: string
  idProject?: string
  scopeLabel: string
  config?: ExistingConfig
}

const schema = z
  .object({
    limitUsd: z
      .string()
      .trim()
      .min(1, 'Budget is required')
      .refine((value) => {
        const parsed = Number(value)
        return Number.isFinite(parsed) && parsed > 0
      }, 'Must be greater than 0'),
    emails: z
      .array(
        z.object({
          value: z
            .string()
            .trim()
            .min(1, 'Email is required')
            .email('Invalid email'),
        })
      )
      .min(1, 'At least one recipient is required'),
    triggers: z
      .array(
        z.object({
          percentage: z
            .string()
            .trim()
            .min(1, 'Percentage is required')
            .refine((value) => {
              const parsed = Number(value)
              return Number.isInteger(parsed) && parsed >= 1 && parsed <= 100
            }, 'Must be a whole number between 1 and 100'),
        })
      )
      .min(1, 'At least one threshold is required'),
  })
  .superRefine((data, ctx) => {
    const seenPercents = new Map<number, number>()
    data.triggers.forEach(({ percentage }, i) => {
      const parsed = Number(percentage)
      if (!Number.isFinite(parsed)) {
        return
      }
      if (seenPercents.has(parsed)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Duplicate threshold',
          path: ['triggers', i, 'percentage'],
        })
      }
      seenPercents.set(parsed, i)
    })

    const seenEmails = new Map<string, number>()
    data.emails.forEach(({ value }, i) => {
      const lower = value.trim().toLowerCase()
      if (!lower) {
        return
      }
      if (seenEmails.has(lower)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Duplicate email',
          path: ['emails', i, 'value'],
        })
      }
      seenEmails.set(lower, i)
    })
  })

type FormValues = z.infer<typeof schema>

const buildDefaults = (config?: ExistingConfig): FormValues => {
  if (config) {
    return {
      limitUsd: String(config.limitUsd),
      emails: config.emails.map((value) => ({ value })),
      triggers: config.triggers.map(({ percentage }) => ({
        percentage: String(percentage),
      })),
    }
  }

  return {
    limitUsd: '',
    emails: [{ value: '' }],
    triggers: [{ percentage: '80' }, { percentage: '100' }],
  }
}

export const UsageAlertSheet = ({
  isOpen,
  onClose,
  idCompany,
  idProject,
  scopeLabel,
  config,
}: Props) => {
  const isUpdate = Boolean(config)
  const { track } = useMixpanel()
  const [isPending, startTransition] = useTransition()

  const [createConfig] = useMutation<
    CreateUsageAlertConfigMutation,
    CreateUsageAlertConfigMutationVariables
  >(CREATE_USAGE_ALERT_CONFIG_MUTATION)
  const [updateConfig] = useMutation<
    UpdateUsageAlertConfigMutation,
    UpdateUsageAlertConfigMutationVariables
  >(UPDATE_USAGE_ALERT_CONFIG_MUTATION)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: buildDefaults(config),
    mode: 'onBlur',
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = form

  const emailsArray = useFieldArray({ control, name: 'emails' })
  const triggersArray = useFieldArray({ control, name: 'triggers' })

  useEffect(() => {
    if (isOpen) {
      reset(buildDefaults(config))
    }
  }, [isOpen, config, reset])

  const onSubmit = (values: FormValues) => {
    if (isPending) {
      return
    }

    const limitUsd = Number(values.limitUsd)
    const emails = values.emails.map(({ value }) => value.trim())
    const triggers = values.triggers.map(({ percentage }) => ({
      percentage: Number(percentage),
    }))

    startTransition(async () => {
      try {
        if (isUpdate && config) {
          await updateConfig({
            variables: { id: config.id, limitUsd, emails, triggers },
            refetchQueries: [USAGE_ALERT_CONFIG_QUERY],
            awaitRefetchQueries: true,
          })
          toast(
            <ToasterContent variant="success">
              Usage alert updated
            </ToasterContent>,
            { position: 'top-right' }
          )
        } else {
          await createConfig({
            variables: {
              idCompany,
              idProject,
              limitUsd,
              emails,
              triggers,
            },
            refetchQueries: [USAGE_ALERT_CONFIG_QUERY],
            awaitRefetchQueries: true,
          })
          toast(
            <ToasterContent variant="success">
              Usage alert created
            </ToasterContent>,
            { position: 'top-right' }
          )
          track('Usage alert enabled', {
            budgetLimit: limitUsd,
            thresholdCount: triggers.length,
            thresholdValues: triggers.map((t) => t.percentage),
          })
        }
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            {isUpdate
              ? 'Failed to update usage alert'
              : 'Failed to create usage alert'}
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  const title = isUpdate ? 'Update usage alert' : 'Add usage alert'
  const submitLabel = isUpdate ? 'Update alert' : 'Create alert'

  return (
    <Sheet open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <SheetContent className="sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            Get email notifications when {scopeLabel.toLowerCase()} spend
            crosses the percentage thresholds you configure.
          </SheetDescription>
        </SheetHeader>
        <div className="px-4 overflow-y-auto w-full pb-2">
          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="grid gap-6"
              noValidate
            >
              <FormField
                control={control}
                name="limitUsd"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Budget limit (USD)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="0.01"
                        autoComplete="off"
                        placeholder="600"
                        aria-label="Budget limit in USD"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <FormLabel>Percentage thresholds</FormLabel>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => triggersArray.append({ percentage: '' })}
                  >
                    <Plus />
                    Add threshold
                  </Button>
                </div>
                <div className="space-y-2">
                  {triggersArray.fields.map((field, index) => (
                    <FormField
                      key={field.id}
                      control={control}
                      name={`triggers.${index}.percentage`}
                      render={({ field: pctField }) => (
                        <FormItem>
                          <div className="flex items-start gap-2">
                            <FormControl>
                              <div className="flex items-center w-full">
                                <Input
                                  type="number"
                                  inputMode="numeric"
                                  min={1}
                                  max={100}
                                  step={1}
                                  autoComplete="off"
                                  aria-label={`Threshold ${index + 1} percentage`}
                                  className="rounded-r-none"
                                  {...pctField}
                                />
                                <div className="border p-1 rounded-md h-9 rounded-l-none border-l-0 shadow-xs px-3 py-1 text-xs flex items-center bg-secondary">
                                  %
                                </div>
                              </div>
                            </FormControl>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={triggersArray.fields.length <= 1}
                              onClick={() => triggersArray.remove(index)}
                              aria-label={`Remove threshold ${index + 1}`}
                            >
                              <Trash2 />
                            </Button>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <FormLabel>Email recipients</FormLabel>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => emailsArray.append({ value: '' })}
                  >
                    <Plus />
                    Add recipient
                  </Button>
                </div>
                <div className="space-y-2">
                  {emailsArray.fields.map((field, index) => (
                    <FormField
                      key={field.id}
                      control={control}
                      name={`emails.${index}.value`}
                      render={({ field: emailField }) => (
                        <FormItem>
                          <div className="flex items-start gap-2">
                            <FormControl>
                              <Input
                                type="email"
                                autoComplete="off"
                                placeholder="alerts@example.com"
                                aria-label={`Recipient ${index + 1}`}
                                {...emailField}
                              />
                            </FormControl>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={emailsArray.fields.length <= 1}
                              onClick={() => emailsArray.remove(index)}
                              aria-label={`Remove recipient ${index + 1}`}
                            >
                              <Trash2 />
                            </Button>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </div>

              <Button type="submit" hidden />
            </form>
          </Form>
        </div>
        <SheetFooter className="sticky">
          <Button
            disabled={isPending || isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            {isPending ? <Loader className="animate-spin" /> : null}
            {submitLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
